import { visibleTime } from './business-settings'
// The order a day reads in — and why it is no longer just the order it is
// stored in.
//
// Lewis, 19 September 2026, after a fortnight of running the round on it:
//
//   "I think even when I book in a time, it doesn't put them necessarily in
//    order. I've got to bump it up. So I put Linda at the top for this
//    Thursday coming, and then every time I've confirmed someone, it's all
//    messy. Is there a way of it auto putting anyone with times at the top, in
//    time order; anyone below that as people that are confirmed and have said
//    that they'll have it done that day; and then it goes on to the yellow
//    boxes below that? Basically instead of pushing the up and down button,
//    which takes a bit of time as well."
//
// So a day sorts itself into three bands:
//
//   1. a time was agreed — in time order, because the times ARE the order
//   2. the day was agreed, no time yet
//   3. nobody has agreed anything yet (the pale rows)
//
// Inside bands 2 and 3 the hand-made order still wins. That order is the
// driving route: he works out from the map which end of the round to start,
// and nudges rows until the day reads in that order. Only band 1 needs no
// nudging.
//
// It also undoes a trap the arrows used to set, which is most of what "it's
// all messy" was. /api/reorder stamps a sort_order on EVERY row of the day the
// first time an arrow is tapped, and the stored order (`sort_order IS NULL,
// sort_order, time_slot`) puts every row that HAS one above every row that
// does not. So one nudge pinned the whole day, `time_slot` never broke another
// tie, and a visit booked later landed at the bottom however early its time.

export type DayRow = {
  id: number
  time_slot?: string | null
  confirmed?: number | null
  state?: string | null
}

// 0 timed · 1 agreed, no time · 2 nobody has agreed · 3 the machine's own
// suggestions, which keep their own group under the day and never mix in.
export type Band = 0 | 1 | 2 | 3

export function bandOf(row: DayRow, suggested = false): Band {
  if (suggested) return 3
  // A time only places a row when the round gives set times (Your business): hidden, it is dragged like any other.
  if (visibleTime(row)) return 0
  // A visit that HAPPENED belongs with the agreed ones, never down among the
  // ones nobody has answered about. Most imported visits carry confirmed = 0
  // whatever became of them, so reading a past day by `confirmed` alone would
  // scatter finished work through the bottom band.
  return row.confirmed || row.state === 'done' ? 1 : 2
}

// Minutes past midnight, for the shapes a time actually arrives in here:
// "09:30" from the time picker, "08:00:00" out of the old spreadsheet, and
// hand-typed "9am" / "2.30pm" / "2:30 PM". Anything else is not a time this
// can order by, and gets null — those rows keep their band but fall to the
// end of it, in their stored order, rather than being sorted by accident.
export function minutesOfDay(raw: string | null | undefined): number | null {
  const s = (raw ?? '').trim().toLowerCase()
  if (!s) return null
  const m = /^(\d{1,2})(?:[:.](\d{2}))?(?::\d{2})?\s*(am|pm)?/.exec(s)
  if (!m) return null
  let hour = Number(m[1])
  const mins = m[2] ? Number(m[2]) : 0
  const half = m[3]
  if (mins > 59) return null
  if (half) {
    if (hour < 1 || hour > 12) return null
    if (half === 'pm' && hour !== 12) hour += 12
    if (half === 'am' && hour === 12) hour = 0
  } else if (hour > 23) return null
  return hour * 60 + mins
}

const never = (): boolean => false

// The whole day, in reading order. Stable: two rows that tie anywhere keep the
// order they arrived in, which is the server's (sort_order, then id).
export function orderDay<T extends DayRow>(rows: readonly T[], isSuggested: (row: T) => boolean = never): T[] {
  return rows.map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const ba = bandOf(a.row, isSuggested(a.row))
      const bb = bandOf(b.row, isSuggested(b.row))
      if (ba !== bb) return ba - bb
      if (ba === 0) {
        const ta = minutesOfDay(visibleTime(a.row))
        const tb = minutesOfDay(visibleTime(b.row))
        // A time nobody can read sits after every time that can be.
        if (ta === null && tb !== null) return 1
        if (tb === null && ta !== null) return -1
        if (ta !== null && tb !== null && ta !== tb) return ta - tb
      }
      return a.i - b.i
    })
    .map(({ row }) => row)
}

// Which rows an arrow may swap: anything except a timed one. A timed row has
// no arrows — its place is its time, and the way to move it is to change the
// time. This is what stops the arrows fighting the bands.
export function canReorder<T extends DayRow>(row: T, isSuggested: (row: T) => boolean = never): boolean {
  return bandOf(row, isSuggested(row)) !== 0
}

// The day after moving one row one place within its own band, or null when
// there is nowhere for it to go. The caller sends the whole returned day to
// /api/reorder, so the stored order and the read order stay the same thing.
// Nothing calls nudge since the arrows came off (Sep 2026); kept because it
// is tested and it is the door the arrows would come back through.
export function nudge<T extends DayRow>(rows: readonly T[], id: number, direction: -1 | 1, isSuggested: (row: T) => boolean = never): T[] | null {
  const day = orderDay(rows, isSuggested)
  const from = day.findIndex(r => r.id === id)
  if (from < 0 || !canReorder(day[from]!, isSuggested)) return null
  const band = bandOf(day[from]!, isSuggested(day[from]!))
  let to = from + direction
  while (to >= 0 && to < day.length && bandOf(day[to]!, isSuggested(day[to]!)) !== band) to += direction
  if (to < 0 || to >= day.length || bandOf(day[to]!, isSuggested(day[to]!)) !== band) return null
  const moved = day.slice()
  ;[moved[from], moved[to]] = [moved[to]!, moved[from]!]
  return moved
}

// The day after a row is dragged onto another row of the same day, or null
// when the drop is not a move. One function for the whole drop, as nudge is
// for one arrow tap: a timed row is out of bounds in either hand (its place
// is its time), a row cannot land outside its own band, and a drop on the
// dragged row itself, or on an id the day does not hold, moves nothing. The
// dragged row takes the target's place and the rows between slide over — a
// drag is not a swap. The caller sends the whole returned day to /api/reorder.
export function draggedTo<T extends DayRow>(rows: readonly T[], draggedId: number, targetId: number, isSuggested: (row: T) => boolean = never): T[] | null {
  if (draggedId === targetId) return null
  const day = orderDay(rows, isSuggested)
  const from = day.findIndex(r => r.id === draggedId)
  const to = day.findIndex(r => r.id === targetId)
  if (from < 0 || to < 0) return null
  const band = bandOf(day[from]!, isSuggested(day[from]!))
  if (band === 0 || band !== bandOf(day[to]!, isSuggested(day[to]!))) return null
  const moved = day.slice()
  const [dragged] = moved.splice(from, 1)
  moved.splice(to, 0, dragged!)
  return moved
}

// ---- the drag that replaced the arrows (September 2026) ---------------------
//
// The arrows came off after the operator ruled "that's not how I wanted it",
// and the HTML5 drag that shared their row never fired from a touch screen at
// all — and the round is run on a phone. A day row is now moved with POINTER
// events: one gesture that behaves the same for a finger, a trackpad and a
// mouse. Everything below is the whole policy of that gesture, testable with
// no browser; the listening in schedule.ts is only the plumbing that feeds it.

export const GRAB_MOVE_PX = 5 // a mouse grabs once it has moved this far, button held
export const LONG_PRESS_MS = 400 // a finger grabs after holding still this long
export const SCROLL_SLOP_PX = 10 // a finger past this is a scroll, not a grab
export const AUTO_SCROLL_EDGE_PX = 60 // the window scrolls itself inside this distance of its edge
export const AUTO_SCROLL_MAX_PX = 12 // ... at up to this many px per frame
export const ROW_HIT_PAD_PX = 6 // about the gap the diary's rows keep

// Should this press become a grab, from what the pointer has done since it
// went down? Mouse or trackpad: the grab starts once the pointer moves ~5px
// with the button held — a click that never moves stays a click. Finger or
// pen: a long press of ~400ms with the finger still roughly in place, because
// an instant grab would make the day impossible to scroll; a finger that
// travels more than ~10px first is a scroll, and the press is given up
// without ever having stopped it. 'wait' means: not yet, keep watching.
export function shouldGrab(pointerType: string, dxPx: number, dyPx: number, msHeld: number): 'grab' | 'abandon' | 'wait' {
  const moved = Math.hypot(dxPx, dyPx)
  if (pointerType === 'mouse') return moved >= GRAB_MOVE_PX ? 'grab' : 'wait'
  if (moved > SCROLL_SLOP_PX) return 'abandon'
  return msHeld >= LONG_PRESS_MS ? 'grab' : 'wait'
}

// A press that landed on a control must stay a tap: ⋯ opens its menu,
// Confirmed confirms, the call link calls. The row's customer link is the
// exception, not a control: it wraps the name, the address and the badges —
// most of the row's face — so a press on it must be able to become a drag
// (the grab thresholds above decide, and a held release is swallowed before
// it can reach the link as a click), while a tap on it still opens the
// customer. `tag` is the nearest interactive ancestor's tag (null when the
// press is on plain face); `insideOpenMenu` is true when the press landed
// anywhere inside an open ⋯ menu, whose whole body must answer whether or
// not it is a tag named here; `onCustomerLink` is true when that nearest
// interactive ancestor is the row's own customer link.
export function pressOnControl(tag: string | null, insideOpenMenu: boolean, onCustomerLink = false): boolean {
  if (insideOpenMenu) return true
  if (onCustomerLink) return false
  return ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 'SUMMARY'].includes(tag ?? '')
}

// Which row the pointer is over: the id of the row whose vertical span holds
// the pointer, or null when it is over neither. Spans are viewport Y ranges
// in reading order; padPx widens each row so the small gaps between stacked
// rows still belong to somebody.
export function rowIdAt(rows: readonly { id: number; top: number; bottom: number }[], y: number, padPx: number = ROW_HIT_PAD_PX): number | null {
  for (const r of rows) if (y >= r.top - padPx && y <= r.bottom + padPx) return r.id
  return null
}

// Where a held row would land: the row under the pointer, with the held
// row's own span left out. The span cannot simply be dropped from the
// geometry the page reports — while held, the row follows the finger through
// a translate, and the browser folds that translate into its rect, so the
// held span travels with the pointer and in reading order sits ahead of
// every row below it. Fed to rowIdAt unfiltered, a drag DOWNWARD always
// resolves to the dragged row itself (draggedTo(x, x) is null), which is why
// nothing downward ever saved. The held row is never a landing place at all:
// excluding it here, once, is what lets the landing line and the drop share
// one answer.
export function dropTargetId(rows: readonly { id: number; top: number; bottom: number }[], heldId: number, y: number, padPx: number = ROW_HIT_PAD_PX): number | null {
  return rowIdAt(rows.filter(r => r.id !== heldId), y, padPx)
}

// Which side of the row under the pointer the dragged row will land on — the
// same rule draggedTo applies: coming from below, it takes the place ABOVE
// the target; coming from above, the place BELOW it. The sides are the
// target's, so the landing line can be drawn before the drop.
export function dropSide(dayIds: readonly number[], draggedId: number, targetId: number): 'above' | 'below' {
  return dayIds.indexOf(draggedId) > dayIds.indexOf(targetId) ? 'above' : 'below'
}

// How fast the page should scroll itself while a held row waits near the top
// or bottom edge (px per frame): 0 outside the zone, gathering to the
// maximum at the edge itself. Without this a phone cannot move a job past
// the fold, which is the common case.
export function autoScrollSpeed(pxToEdge: number): number {
  if (pxToEdge >= AUTO_SCROLL_EDGE_PX) return 0
  const nearness = 1 - Math.max(0, pxToEdge) / AUTO_SCROLL_EDGE_PX
  return Math.max(2, Math.round(AUTO_SCROLL_MAX_PX * nearness))
}

// ---- a time pencilled in by dragging a visit above one with a time (tidy-up item 7) -----------------------------------
//
// Liam, 20 Oct 2026: "when i drag Joel it should pencil a time in for him earlier than the others hence the drag?". Only
// times written as times count (Astra's review): "10:30", "9am", "9:30am", "2.30pm", "12pm", the whole of what was
// typed ("08:00:00" from an old spreadsheet too, its seconds nothing). "9am-11am" or "10 minutes" is not one: minutesOfDay
// reads such a time's start to sort it, but no time is worked out from it.
export function strictMinutes(raw: string | null | undefined): number | null {
  const s = (raw ?? '').trim().toLowerCase()
  let m = /^([01]?\d|2[0-3]):([0-5]\d)(?::00)?$/.exec(s)
  if (m) return Number(m[1]) * 60 + Number(m[2])
  m = /^(1[0-2]|0?[1-9])(?:[:.]([0-5]\d))?\s*(am|pm)$/.exec(s)
  if (!m) return null
  return (Number(m[1]) % 12 + (m[3] === 'pm' ? 12 : 0)) * 60 + (m[2] ? Number(m[2]) : 0)
}

// "09:05": what is saved for a pencilled time, the time picker's own shape.
export function clockOf(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}

export const PENCIL_EARLIEST = 6 * 60 // nothing is pencilled before 6am
export const PENCIL_GAP = 20 // two times closer than this have no room between them

export type PencilAnswer = { minutes: number } | { refused: 'close' | 'gone' }
// The time a visit takes when dropped above `next` (the first stop with a time below the drop), with `prev` the one
// above it (null when the drop is above the first). Lengths are minutes when known (Sonnet's edge cases, 20 Oct 2026):
//   above the first, t1: it ends by t1, so t1 minus its own length (an hour when not known); before 6am, halfway
//     between 6am and t1, rounded to 5 minutes, when they are at least 20 minutes apart; else no room;
//   between t1 and t2: from the end of the earlier visit when its length is known, if it fits before t2; with no length,
//     halfway, rounded to 5 minutes, when they are at least 20 minutes apart; else no room; 6am the earliest here too;
//   a known length of its own must end by the time below;
//   `now` (minutes, on today only): a time already gone is not pencilled.
export function pencilTime(prev: { at: number; length?: number | null } | null, next: { at: number }, own: number | null = null, now: number | null = null): PencilAnswer {
  const halfway = (a: number, b: number): number => Math.round((a + b) / 2 / 5) * 5
  let at: number | null = null
  if (!prev) {
    const t1 = next.at
    if (t1 > PENCIL_EARLIEST) {
      const start = t1 - (own ?? 60)
      if (start >= PENCIL_EARLIEST) at = start
      else if (t1 - PENCIL_EARLIEST >= PENCIL_GAP) at = halfway(PENCIL_EARLIEST, t1)
    }
  } else if (prev.length != null && prev.length > 0) {
    // 6am the earliest between two times too (Astra's final review, 6, 20 Oct 2026): from 6am when the earlier one ends
    // before it, halfway from 6am when it sits before it.
    const start = Math.max(prev.at + prev.length, PENCIL_EARLIEST)
    if (start < next.at) at = start
  } else if (next.at - Math.max(prev.at, PENCIL_EARLIEST) >= PENCIL_GAP) at = halfway(Math.max(prev.at, PENCIL_EARLIEST), next.at)
  if (at === null || at < PENCIL_EARLIEST || at >= next.at || (prev && at <= prev.at) || (own != null && own > 0 && at + own > next.at)) return { refused: 'close' }
  if (now !== null && at <= now) return { refused: 'gone' }
  return { minutes: at }
}
